import React, { useState, useRef, useEffect, forwardRef, useImperativeHandle } from "react";
import { cn } from "@/lib/utils";

interface VideoWithShimmerProps extends React.VideoHTMLAttributes<HTMLVideoElement> {
  containerClassName?: string;
  shimmerClassName?: string;
}

const VideoWithShimmer = forwardRef<HTMLVideoElement, VideoWithShimmerProps>(
  ({ className, containerClassName, shimmerClassName, onLoadedData, ...props }, ref) => {
    const [isLoaded, setIsLoaded] = useState(false);
    const videoRef = useRef<HTMLVideoElement>(null);

    // Properly expose the video element ref
    useImperativeHandle(ref, () => videoRef.current!);

    useEffect(() => {
      if (videoRef.current && videoRef.current.readyState >= 2) {
        setIsLoaded(true);
      }
    }, []);

    const handleLoadedData = (e: React.SyntheticEvent<HTMLVideoElement, Event>) => {
      setIsLoaded(true);
      if (onLoadedData) {
        onLoadedData(e);
      }
    };

    return (
      <div className={cn("relative overflow-hidden", containerClassName)}>
        {!isLoaded && (
          <div
            className={cn(
              "absolute inset-0 shimmer z-10",
              shimmerClassName
            )}
          />
        )}
        <video
          ref={videoRef}
          onLoadedData={handleLoadedData}
          className={cn(
            className,
            !isLoaded ? "opacity-0" : "opacity-100",
            "transition-opacity duration-500"
          )}
          {...props}
        />
      </div>
    );
  }
);

VideoWithShimmer.displayName = "VideoWithShimmer";

export { VideoWithShimmer };
